import type { ModuleContext, ModuleSpec, NavModuleHandle, NavModuleInit, Trigger } from './types';

export class Registry {
  private specs: ModuleSpec[] = [];
  private mounted = new Map<string, NavModuleHandle>();
  private pending = new Set<string>();
  private triggerCleanups: Array<() => void> = [];
  private disposed = false;

  constructor(
    private root: HTMLElement,
    private ctx: ModuleContext,
  ) {}

  register(spec: ModuleSpec): this {
    if (this.specs.some((s) => s.name === spec.name)) {
      throw new Error(`[global-nav-v2] duplicate module name "${spec.name}"`);
    }
    const hasInline = typeof spec.inline === 'function';
    const hasLoad = typeof spec.load === 'function';
    if (hasInline === hasLoad) {
      throw new Error(
        `[global-nav-v2] module "${spec.name}" must specify EXACTLY ONE of: inline | load`,
      );
    }
    this.specs.push(spec);
    return this;
  }

  start(): void {
    for (const spec of this.specs) this.schedule(spec);
  }

  async require(name: string): Promise<void> {
    if (this.disposed) return;
    const spec = this.specs.find((s) => s.name === name);
    if (spec) await this.mount(spec);
  }

  isMounted(name: string): boolean {
    return this.mounted.has(name);
  }

  list(): string[] {
    return this.specs.map((s) => s.name);
  }

  destroy(): void {
    this.disposed = true;
    for (const cleanup of this.triggerCleanups) cleanup();
    this.triggerCleanups = [];
    for (const h of this.mounted.values()) h.destroy();
    this.mounted.clear();
    this.pending.clear();
  }

  private schedule(spec: ModuleSpec): void {
    if (spec.selector && !document.querySelector(spec.selector)) return;
    const triggers = Array.isArray(spec.trigger) ? spec.trigger : [spec.trigger];
    void this.runWithTriggers(spec, triggers);
  }

  private async runWithTriggers(spec: ModuleSpec, triggers: Trigger[]): Promise<void> {
    try {
      await Promise.all(triggers.map((t) => this.awaitTrigger(t, spec)));
      if (this.disposed) return;
      await this.mount(spec);
    } catch {}
  }

  private awaitTrigger(t: Trigger, spec: ModuleSpec): Promise<void> {
    return new Promise((resolve, reject) => {
      switch (t.type) {
        case 'immediate':
          resolve();
          return;

        case 'idle': {
          const ric = (
            window as Window & {
              requestIdleCallback?: (cb: () => void) => number;
            }
          ).requestIdleCallback;
          if (ric) ric(() => resolve());
          else setTimeout(() => resolve(), 200);
          return;
        }

        case 'media': {
          const mql = matchMedia(t.query);
          if (mql.matches) {
            resolve();
            return;
          }
          const onChange = (e: MediaQueryListEvent) => {
            if (e.matches) {
              mql.removeEventListener('change', onChange);
              resolve();
            }
          };
          mql.addEventListener('change', onChange);
          this.triggerCleanups.push(() => mql.removeEventListener('change', onChange));
          return;
        }

        case 'interaction': {
          const el = spec.selector ? document.querySelector(spec.selector) : this.root;
          if (!el) {
            reject();
            return;
          }
          const once = () => {
            el.removeEventListener(t.on, once);
            resolve();
          };
          el.addEventListener(t.on, once, { passive: true });
          this.triggerCleanups.push(() => el.removeEventListener(t.on, once));
          return;
        }

        case 'visible': {
          const el = spec.selector ? document.querySelector(spec.selector) : this.root;
          if (!el) {
            reject();
            return;
          }
          const obs = new IntersectionObserver(
            (entries) => {
              for (const e of entries) {
                if (e.isIntersecting) {
                  obs.disconnect();
                  resolve();
                  return;
                }
              }
            },
            { rootMargin: t.rootMargin ?? '50px' },
          );
          obs.observe(el);
          this.triggerCleanups.push(() => obs.disconnect());
          return;
        }

        case 'login': {
          if (this.ctx.session.isLoggedIn()) {
            resolve();
            return;
          }
          const off = this.ctx.session.onAuthChange(() => {
            if (this.ctx.session.isLoggedIn()) {
              off();
              resolve();
            }
          });
          this.triggerCleanups.push(off);
          return;
        }

        case 'when': {
          (async () => {
            try {
              const ok = await t.predicate();
              if (ok) resolve();
              else reject(new Error('predicate returned false'));
            } catch (e) {
              reject(e instanceof Error ? e : new Error(String(e)));
            }
          })();
          return;
        }
      }
    });
  }

  private async mount(spec: ModuleSpec): Promise<void> {
    if (this.disposed) return;
    if (this.mounted.has(spec.name) || this.pending.has(spec.name)) return;
    this.pending.add(spec.name);
    try {
      const init: NavModuleInit = spec.inline ?? (await spec.load!()).default;
      const handle = await init(this.ctx);
      // If the registry was destroyed while async init was in flight, tear
      // the just-created handle down so its listeners don't outlive the
      // root it was bound to.
      if (this.disposed) {
        handle?.destroy();
        return;
      }
      if (handle) this.mounted.set(spec.name, handle);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[global-nav-v2] module "${spec.name}" failed`, e);
    } finally {
      this.pending.delete(spec.name);
    }
  }
}
